import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { AppState, GetState, SetState } from './state';
import { createAuthSlice } from './slices/auth';
import { createBootSlice } from './slices/boot';
import { createCameraSlice } from './slices/camera';
import { createDocsSlice } from './slices/docs';
import { createFiltersSlice } from './slices/filters';
import { createGraphSlice } from './slices/graph';
import { createPositionsSlice } from './slices/positions';
import { createPresenceSlice } from './slices/presence';
import { createSizesSlice } from './slices/sizes';
import { createUiSlice } from './slices/ui';

/**
 * The store. One instance, assembled from the nine slices the plan names plus the boot
 * machine it also asks for.
 *
 * `subscribeWithSelector` is not decoration. It is what lets a subscriber outside React watch
 * ONE value — `useAppStore.subscribe(s => s.positions[id], fn)` — which is how a `MapNode`
 * avoids re-rendering when a different node moves, and how the wire layer can patch `d`
 * attributes on the same frame as a drag without a render at all.
 *
 * `getState` / `setState` are re-exported because the drag path is not a component: a
 * `pointermove` handler reads the camera and the dragged subtree's positions, and commits on
 * `pointerup`. Hooks cannot help there, and routing it through React state would reconcile
 * 147 nodes per frame — the thing this whole layer exists to prevent.
 *
 * WHERE STATE COMES FROM. `app/state.ts` owns the map content and mirrors it here at one write
 * point (`store/fromApp.ts`) on every publish, because a flat cached snapshot is what an
 * ordinary React read wants and a selector-subscribable store is what the map's hot path
 * needs. What the store OWNS outright, with no mirror at all, is everything only the map has a
 * use for: the camera, the measured sizes, the expanded-rows layout, the in-place rename.
 */
export const useAppStore = create<AppState>()(
  subscribeWithSelector((set, get) => {
    const s = set as SetState;
    const g = get as GetState;
    return {
      ...createGraphSlice(s, g),
      ...createPositionsSlice(s, g),
      ...createSizesSlice(s, g),
      ...createCameraSlice(s, g),
      ...createUiSlice(s, g),
      ...createFiltersSlice(s, g),
      ...createDocsSlice(s, g),
      ...createPresenceSlice(s, g),
      ...createAuthSlice(s, g),
      ...createBootSlice(s, g),
    };
  }),
);

/** Read the store from outside React — pointer handlers, the sync queue, tests. */
export const getStoreState = (): AppState => useAppStore.getState();

/** Write from outside React. Prefer an action; this is for the mirror and for tests. */
export const setStoreState = (partial: Partial<AppState>): void => useAppStore.setState(partial);

/** Selector-aware subscribe, for consumers that are not components. */
export const subscribeStore = useAppStore.subscribe;

export type { AppState } from './state';
export type { BootPhase } from './slices/boot';
export { BOOT_ORDER, bootAtLeast, viewWritesAllowed } from './slices/boot';
export type { NodePosition, PositionMap } from './slices/positions';
export type { NodeSize, SizeMap } from './slices/sizes';
export type { Camera } from './slices/camera';
export { MIN_ZOOM, MAX_ZOOM, FIT_MAX_ZOOM, clampZoom } from './slices/camera';
export { DEFAULT_SEARCH_SCOPE, normaliseQuery } from './slices/filters';
